import { ReactNode, Suspense } from 'react';
import LazyLoadErrorBoundary from '@/components/LazyLoadErrorBoundary.tsx';
import StyledModal from '@/components/StyledModal/StyledModal.tsx';
import CustomSpinner from '@/components/Spinner.tsx';

type Props = {
  children: ReactNode;
  resetKey: string;
  title: string;
  onClose: () => void;
  closeLabel?: string;
};

// The loaded component owns its normal modal. Loading and failure states need
// their own shell so the user can always close the dialog and keep working.
export default function LazyModalBoundary({
  children,
  resetKey,
  title,
  onClose,
  closeLabel = `Close ${title}`,
}: Props) {
  const shell = (content: ReactNode) => (
    <StyledModal
      opened
      onClose={onClose}
      title={title}
      withCloseButton
      closeButtonProps={{ 'aria-label': closeLabel }}
    >
      {content}
    </StyledModal>
  );
  return (
    <LazyLoadErrorBoundary resetKey={resetKey} fallback={shell}>
      <Suspense
        fallback={shell(
          <div role="status" aria-label={`Loading ${title}`}>
            <CustomSpinner />
          </div>,
        )}
      >
        {children}
      </Suspense>
    </LazyLoadErrorBoundary>
  );
}
